import React, { useState } from 'react';
import { WorkflowExecution, NodeExecution } from '../types';
import { Clock, CheckCircle, XCircle, PlayCircle, AlertCircle, ChevronDown, ChevronUp, ExternalLink, Timer, Hash, User, ThumbsUp, MessageSquare, Share2, TrendingUp, Bell, UserPlus, Mail, Send, MessageCircle, Image, Wand2, FileText, Heart } from 'lucide-react';
import { format } from 'date-fns';

interface ExecutionDetailProps {
  execution: WorkflowExecution;
  accountNameMap?: Record<string, string>;
}

/* ── Status helpers ──────────────────────────────────────────── */

const STATUS_CFG: Record<string, { icon: (s: number) => React.ReactNode; color: string; bg: string; border: string; gradient: string; dotColor: string }> = {
  completed: {
    icon: (s) => <CheckCircle size={s} />,
    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0',
    gradient: 'linear-gradient(135deg, #22c55e, #16a34a)', dotColor: '#22c55e',
  },
  failed: {
    icon: (s) => <XCircle size={s} />,
    color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
    gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', dotColor: '#ef4444',
  },
  running: {
    icon: (s) => <PlayCircle size={s} />,
    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe',
    gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', dotColor: '#3b82f6',
  },
  partial: {
    icon: (s) => <AlertCircle size={s} />,
    color: '#ea580c', bg: '#fff7ed', border: '#fed7aa',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)', dotColor: '#f97316',
  },
  partially_completed: {
    icon: (s) => <AlertCircle size={s} />,
    color: '#ea580c', bg: '#fff7ed', border: '#fed7aa',
    gradient: 'linear-gradient(135deg, #f97316, #ea580c)', dotColor: '#f97316',
  },
  pending: {
    icon: (s) => <Clock size={s} />,
    color: '#64748b', bg: '#f8fafc', border: '#e2e8f0',
    gradient: 'linear-gradient(135deg, #94a3b8, #64748b)', dotColor: '#94a3b8',
  },
};

const getCfg = (status: string) => STATUS_CFG[status] || STATUS_CFG.pending;

/* ── Helper sub-components ───────────────────────────────────── */

const ResultCard: React.FC<{ title: string; children: React.ReactNode; variant?: 'success' | 'info' | 'danger' }> = ({ title, children, variant = 'success' }) => {
  const isInfo = variant === 'info';
  const isDanger = variant === 'danger';

  let bg = '#f0fdf4';
  let border = '#bbf7d0';
  let titleColor = '#15803d';
  let icon = '✅';

  if (isInfo) {
    bg = '#eff6ff';
    border = '#bfdbfe';
    titleColor = '#1d4ed8';
    icon = '📋';
  } else if (isDanger) {
    bg = '#fef2f2';
    border = '#fecaca';
    titleColor = '#b91c1c';
    icon = '❌';
  }

  return (
    <div style={{
      marginTop: '10px',
      padding: '12px 14px',
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: '10px',
    }}>
      <div style={{
        fontSize: '12px',
        fontWeight: 700,
        color: titleColor,
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
};

const LeadsList: React.FC<{ leads: any[] }> = ({ leads }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? leads : leads.slice(0, 3);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {visible.map((lead: any, idx: number) => (
        <div key={idx} style={{
          padding: '10px 12px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          fontSize: '12px',
          transition: 'border-color 0.15s',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>
              {lead.author || `Post #${lead.id}`}
            </span>
            {lead.url && (
              <a href={lead.url} target="_blank" rel="noopener noreferrer"
                style={{
                  color: '#2563eb', fontSize: '11px', fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  padding: '2px 8px', background: '#eff6ff', borderRadius: '6px',
                  textDecoration: 'none',
                }}>
                Open <ExternalLink size={10} />
              </a>
            )}
          </div>
          {lead.text && (
            <div style={{ color: '#64748b', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {lead.text.length > 200 ? `${lead.text.slice(0, 200)}…` : lead.text}
            </div>
          )}
          {lead.comments_count !== undefined && (
            <div style={{ color: '#94a3b8', marginTop: '6px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              💬 {lead.comments_count} comments
            </div>
          )}
        </div>
      ))}
      {leads.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '8px',
          }}
        >
          {expanded ? (
            <>Show less <ChevronUp size={14} /></>
          ) : (
            <>Show all {leads.length} posts <ChevronDown size={14} /></>
          )}
        </button>
      )}
    </div>
  );
};

const CollapsibleJSON: React.FC<{ data: any }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const json = JSON.stringify(data, null, 2);
  const isLong = json.length > 300;
  return (
    <div>
      <pre style={{
        fontSize: '11px',
        lineHeight: '1.6',
        background: '#f8fafc',
        padding: '10px',
        borderRadius: '8px',
        overflow: 'auto',
        maxHeight: expanded ? 'none' : '120px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: '#334155',
        margin: 0,
        border: '1px solid #e2e8f0',
      }}>
        {expanded || !isLong ? json : `${json.slice(0, 300)}…`}
      </pre>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none', border: 'none',
            color: '#2563eb', cursor: 'pointer',
            fontSize: '12px', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '4px 0', marginTop: '6px',
          }}
        >
          {expanded ? <>Collapse <ChevronUp size={14} /></> : <>Expand <ChevronDown size={14} /></>}
        </button>
      )}
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────────── */

export const ExecutionDetail: React.FC<ExecutionDetailProps> = ({ execution, accountNameMap = {} }) => {
  const hasInternalError = execution.nodes.some(n => !!n.error || n.status === 'failed');
  const hasError = execution.status === 'failed' || hasInternalError;
  const cfg = hasError ? STATUS_CFG.failed : getCfg(execution.status);

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return format(new Date(dateStr), 'MMM d, yyyy HH:mm:ss');
  };

  const renderNodeResult = (node: NodeExecution) => {
    if (node.status !== 'completed' || !node.result) return null;

    const result = node.result;
    const data = result?.data;

    if (node.node_type === 'account_selector' && (data?.account || result?.account)) {
      const account = data?.account || result?.account;
      const accountId = account.account_id;
      const displayName = accountNameMap[accountId] || accountId;

      return (
        <ResultCard title="Account Selected" variant="info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
            <User size={14} style={{ color: '#64748b' }} />
            <span style={{ fontWeight: 600 }}>{displayName}</span>
          </div>
          {displayName !== accountId && (
            <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', marginTop: '4px' }}>
              {accountId}
            </div>
          )}
          {account.validated && (
            <span style={{ display: 'inline-block', marginTop: '6px', fontSize: '11px', fontWeight: 600, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '2px 8px' }}>
              Validated
            </span>
          )}
        </ResultCard>
      );
    }

    if (node.node_type === 'brand_info' && (data?.brand_info || result?.brand_info)) {
      const brandInfo = data?.brand_info || result?.brand_info;
      const config = brandInfo.brand_info_config;
      return (
        <ResultCard title="Brand Info" variant="info">
          <div style={{ display: 'grid', gap: '6px', fontSize: '13px' }}>
            {config?.brand_name && (
              <div style={{ color: '#1e293b' }}>
                <span style={{ fontWeight: 600 }}>Brand:</span> {config.brand_name}
              </div>
            )}
            {config?.industry && (
              <div style={{ color: '#475569' }}>
                <span style={{ fontWeight: 600 }}>Industry:</span> {config.industry}
              </div>
            )}
            {config?.description && (
              <div style={{ color: '#475569' }}>
                <span style={{ fontWeight: 600 }}>Description:</span> {config.description}
              </div>
            )}
            {config?.mention_style && (
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                Style: {config.mention_style}
              </div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'account_filter' && (data || result)) {
      const d = data || result;
      const filtered = d.filtered_accounts || [];
      return (
        <ResultCard title={`Account Filter — ${filtered.length} matching accounts`} variant="info">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {filtered.map((id: string, idx: number) => (
              <span key={idx} style={{ fontSize: '11px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px 8px', color: '#475569' }}>
                {accountNameMap[id] || id}
              </span>
            ))}
            {filtered.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No accounts matched the filter criteria.</div>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'search_pages' && (data || result)) {
      const d = data || result;
      const pages = d.pages || [];
      return (
        <ResultCard title={`Search Pages — Found ${d.pages_found ?? pages.length} pages`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pages.map((p: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {p.picture && <img src={p.picture} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />}
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.name || p.page_name || 'Page'}</span>
                </div>
                {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}><ExternalLink size={12} /></a>}
              </div>
            ))}
            {pages.length === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No pages found.</div>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'seeding_contents_manager' && (data || result)) {
      const d = data || result;
      const accounts = d.seeding_accounts || d.seeding_contents_manager_config?.seeding_accounts || [];
      const managerSummary = d.seeding_contents_manager;

      return (
        <ResultCard title={managerSummary?.name ? `Seeding Manager — ${managerSummary.name}` : `Seeding Manager — ${accounts.length || managerSummary?.account_count || 0} accounts`} variant="info">
          <div style={{ display: 'grid', gap: '8px' }}>
            {accounts.length > 0 ? (
              accounts.map((acc: any, idx: number) => (
                <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{accountNameMap[acc.account_id] || acc.account_id}</span>
                    <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', textTransform: 'capitalize' }}>{acc.post_mode || 'Follow'}</span>
                  </div>
                  {acc.post_urls && acc.post_urls.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#475569', marginBottom: '4px' }}>
                      🎯 Targets: <strong>{acc.post_urls.length}</strong> manual URL(s)
                    </div>
                  )}
                  {acc.frequency_control && (
                    <div style={{ display: 'flex', gap: '10px', marginTop: '6px', fontSize: '11px', color: '#94a3b8' }}>
                      <span title="Max total per post">Total: {acc.frequency_control.max_total_per_post || '∞'}</span>
                      <span title="Max per day per post">Daily: {acc.frequency_control.max_per_day_per_post || '∞'}</span>
                      <span title="Cooldown hours">Wait: {acc.frequency_control.cooldown_hours}h</span>
                    </div>
                  )}
                </div>
              ))
            ) : managerSummary ? (
              <div style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#334155' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Tên cấu hình: <strong>{managerSummary.name}</strong></span>
                  <span style={{ fontSize: '11px', color: '#16a34a', background: '#f0fdf4', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                    {managerSummary.account_count} tài khoản
                  </span>
                </div>
                {d.stored_in_context && <div style={{ marginTop: '6px', fontSize: '11px', color: '#94a3b8' }}>✅ Đã lưu vào bộ nhớ thực thi</div>}
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>No seeding accounts configured.</div>
            )}
          </div>
        </ResultCard>
      );
    }

    const filterItems = data?.leads || data?.posts || result?.leads || result?.posts;
    const singleFilterItem = !filterItems && (data?.url || result?.url) ? (data || result) : null;
    if (node.node_type === 'filter_post' && (filterItems || singleFilterItem)) {
      const displayItems = Array.isArray(filterItems) ? filterItems : singleFilterItem ? [singleFilterItem] : [];
      return (
        <ResultCard title={`Filter Posts — ${displayItems.length} posts found`}>
          <LeadsList leads={displayItems} />
        </ResultCard>
      );
    }

    if (node.node_type === 'find_group_posts' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title={`Find Group Posts — ${d.posts_found ?? 0} posts found`}>
          <div style={{ display: 'grid', gap: '6px', fontSize: '13px' }}>
            <div style={{ color: '#475569' }}>Groups scanned: <strong>{d.groups_scanned ?? 'N/A'}</strong></div>
            {d.skipped && (
              <span style={{ display: 'inline-block', fontSize: '11px', fontWeight: 600, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '2px 8px' }}>
                Skipped (dedup)
              </span>
            )}
            {(d.leads || d.posts) && (
              <LeadsList leads={d.leads || d.posts} />
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'find_trend_fb' && (data || result)) {
      const d = data || result;
      const items = d.leads || d.posts || [];
      const singleItem = items.length === 0 && (d.url || d.text || d.summary) ? d : null;
      const displayItems = items.length > 0 ? items : singleItem ? [singleItem] : [];
      const stats = d.discovery_stats;

      return (
        <ResultCard title={`Trending Posts — ${displayItems.length} found`}>
          <div style={{ display: 'grid', gap: '12px' }}>
            {stats && (
              <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <span>Quét: <strong>{stats.discovered_count}</strong></span>
                <span>Duyệt: <strong style={{ color: '#16a34a' }}>{stats.accepted_count}</strong></span>
                <span>Bỏ: <strong style={{ color: '#dc2626' }}>{stats.rejected_count}</strong></span>
              </div>
            )}
            {displayItems.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '12px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', display: 'grid', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    {item.search_keyword ? `Keyword: ${item.search_keyword}` : (item.author || `Post #${idx + 1}`)}
                  </div>
                  {item.url && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', padding: '2px 8px', background: '#eff6ff', borderRadius: '6px', textDecoration: 'none' }}>
                      Open <ExternalLink size={10} />
                    </a>
                  )}
                </div>

                {item.image_urls && item.image_urls.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {item.image_urls.map((u: string, i: number) => (
                      <img key={i} src={u} alt="" style={{ height: '100px', borderRadius: '8px', border: '1px solid #f1f5f9', objectFit: 'cover' }} />
                    ))}
                  </div>
                )}

                {(item.text || item.summary) && (
                  <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                    {item.text || item.summary}
                  </div>
                )}

                {item.remake_caption && (
                  <div style={{ padding: '10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Remake Caption</div>
                    <div style={{ color: '#15803d', fontSize: '12px', lineHeight: '1.5' }}>{item.remake_caption}</div>
                  </div>
                )}
              </div>
            ))}
            {displayItems.length === 0 && (
              <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px' }}>
                Không tìm thấy bài viết trending nào phù hợp.
              </div>
            )}
          </div>
        </ResultCard>
      );
    }


    if (node.node_type === 'post_to_profile' && (data || result)) {
      const postResult = data || result;
      return (
        <ResultCard title="Đăng bài profile">
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            {postResult.post_content && (
              <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nội dung bài đăng</div>
                <div style={{ color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '13px' }}>
                  {postResult.post_content.length > 400 ? `${postResult.post_content.slice(0, 400)}…` : postResult.post_content}
                </div>
              </div>
            )}
            {postResult.post_url && (
              <a href={postResult.post_url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontWeight: 500 }}>
                Xem bài đăng <ExternalLink size={12} />
              </a>
            )}
            {postResult.status && (
              <span style={{
                fontSize: '11px', fontWeight: 600, color: postResult.status === 'published' ? '#16a34a' : '#d97706',
                background: postResult.status === 'published' ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${postResult.status === 'published' ? '#bbf7d0' : '#fde68a'}`,
                borderRadius: '6px', padding: '2px 8px', display: 'inline-block'
              }}>
                {postResult.status}
              </span>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'post_to_group' && (data || result)) {
      const d = data || result;
      const posts: any[] = d.posts || [];
      const summary = d.summary;
      const hasFailed = (summary?.failed ?? 0) > 0;
      const allFailed = hasFailed && (summary?.success ?? 0) === 0;

      return (
        <ResultCard
          title={`Đăng bài nhóm — ${summary?.success ?? 0} thành công${summary?.pending_approval ? `, ${summary.pending_approval} chờ duyệt` : ''}${summary?.failed ? `, ${summary.failed} lỗi` : ''}`}
          variant={allFailed ? 'danger' : hasFailed ? 'info' : 'success'}
        >
          <div style={{ display: 'grid', gap: '8px' }}>
            {d.post_content && (
              <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nội dung bài đăng</div>
                <div style={{ color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '13px' }}>
                  {d.post_content.length > 400 ? `${d.post_content.slice(0, 400)}…` : d.post_content}
                </div>
              </div>
            )}
            {posts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {posts.slice(0, 5).map((p: any, idx: number) => (
                  <div key={idx} style={{ padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#475569', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.group_url?.replace('https://www.facebook.com/groups/', 'groups/')}
                      </div>
                      {p.content_snippet && <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.content_snippet}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', shrink: 0 } as any}>
                      {p.status === 'pending_approval'
                        ? <span style={{ fontSize: '11px', fontWeight: 600, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap' }}>⏳ Chờ duyệt</span>
                        : <span style={{ fontSize: '11px', fontWeight: 600, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap' }}>✅ Đã đăng</span>}
                      {p.post_url && <a href={p.post_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}><ExternalLink size={13} /></a>}
                    </div>
                  </div>
                ))}
                {posts.length > 5 && <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>...và {posts.length - 5} nhóm khác</div>}
              </div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'comment_on_post' && (data || result)) {
      const d = data || result;
      // Group posts mode: find_group_posts → comment_on_post
      if (d.group_posts && d.results) {
        return (
          <ResultCard title={`Bình luận — ${d.commented ?? 0}/${d.total_urls ?? 0} bài đã bình luận`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {d.requirement && (
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>
                  📌 Chủ đề: <strong>{d.requirement}</strong>
                </div>
              )}
              {d.results.map((item: any, idx: number) => {
                const success = item.commented;
                const hasError = !!item.error;
                return (
                  <div key={idx} style={{
                    padding: '10px 12px',
                    background: hasError ? '#fef2f2' : success ? 'white' : '#fefce8',
                    borderRadius: '8px',
                    border: `1px solid ${hasError ? '#fecaca' : success ? '#e5e7eb' : '#fde68a'}`,
                    fontSize: '12px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <span style={{
                        fontWeight: 600,
                        color: hasError ? '#dc2626' : success ? '#16a34a' : '#92400e',
                        fontSize: '12px',
                      }}>
                        {hasError ? '❌ Lỗi' : success ? '✅ Đã bình luận' : '⏭ Bỏ qua'}
                      </span>
                      {item.post_url && (
                        <a href={item.post_url} target="_blank" rel="noopener noreferrer"
                          style={{
                            color: '#2563eb', fontSize: '11px', fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', background: '#eff6ff', borderRadius: '6px',
                            textDecoration: 'none', whiteSpace: 'nowrap',
                          }}>
                          Mở <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {item.comment_text && (
                      <div style={{ color: '#334155', fontSize: '12px', lineHeight: '1.5', padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                        💬 {item.comment_text}
                      </div>
                    )}
                    {hasError && item.error && (
                      <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>{item.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </ResultCard>
        );
      }

      // Filter posts mode / Find top posts mode → comment_on_post
      if ((d.filter_posts || d.from_top_posts) && d.results) {
        return (
          <ResultCard title={`Bình luận — ${d.commented ?? 0}/${d.total_urls ?? 0} bài đã bình luận`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {d.results.map((item: any, idx: number) => {
                const success = item.commented;
                const hasError = !!item.error;
                return (
                  <div key={idx} style={{
                    padding: '10px 12px',
                    background: hasError ? '#fef2f2' : success ? 'white' : '#fefce8',
                    borderRadius: '8px',
                    border: `1px solid ${hasError ? '#fecaca' : success ? '#e5e7eb' : '#fde68a'}`,
                    fontSize: '12px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <span style={{
                        fontWeight: 600,
                        color: hasError ? '#dc2626' : success ? '#16a34a' : '#92400e',
                        fontSize: '12px',
                      }}>
                        {hasError ? '❌ Lỗi' : success ? '✅ Đã bình luận' : '⏭ Bỏ qua'}
                      </span>
                      {item.post_url && (
                        <a href={item.post_url} target="_blank" rel="noopener noreferrer"
                          style={{
                            color: '#2563eb', fontSize: '11px', fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', background: '#eff6ff', borderRadius: '6px',
                            textDecoration: 'none', whiteSpace: 'nowrap',
                          }}>
                          Mở <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {item.comment_text && (
                      <div style={{ color: '#334155', fontSize: '12px', lineHeight: '1.5', padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                        💬 {item.comment_text}
                      </div>
                    )}
                    {hasError && item.error && (
                      <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>{item.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </ResultCard>
        );
      }

      // Managed mode: seeding_contents_manager drives comment_on_post
      if (d.managed && d.results) {
        return (
          <ResultCard title={`Bình luận — ${d.commented ?? 0} đã bình luận, ${d.skipped ?? 0} bỏ qua (${d.total_items ?? 0} mục)`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {d.results.map((item: any, idx: number) => {
                const isSkipped = item.skipped;
                const hasError = !!item.error;
                return (
                  <div key={idx} style={{
                    padding: '10px 12px',
                    background: isSkipped ? '#fefce8' : hasError ? '#fef2f2' : 'white',
                    borderRadius: '8px',
                    border: `1px solid ${isSkipped ? '#fde68a' : hasError ? '#fecaca' : '#e5e7eb'}`,
                    fontSize: '12px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <span style={{
                        fontWeight: 600,
                        color: isSkipped ? '#92400e' : hasError ? '#dc2626' : '#16a34a',
                        fontSize: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        {isSkipped ? '⏭ Bỏ qua' : hasError ? '❌ Lỗi' : '✅ Đã bình luận'}
                        {isSkipped && item.reason && (
                          <span style={{ fontWeight: 400, color: '#a16207' }}>({item.reason})</span>
                        )}
                      </span>
                      {item.post_url && (
                        <a href={item.post_url} target="_blank" rel="noopener noreferrer"
                          style={{
                            color: '#2563eb', fontSize: '11px', fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '2px 8px', background: '#eff6ff', borderRadius: '6px',
                            textDecoration: 'none', whiteSpace: 'nowrap',
                          }}>
                          Mở <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {item.post_content_snippet && (
                      <div style={{ color: '#94a3b8', fontSize: '11px', lineHeight: '1.4', marginBottom: '6px', padding: '4px 8px', background: '#f8fafc', borderRadius: '6px', borderLeft: '2px solid #e2e8f0' }}>
                        📄 {item.post_content_snippet.length > 150 ? `${item.post_content_snippet.slice(0, 150)}…` : item.post_content_snippet}
                      </div>
                    )}
                    {item.comment_text && (
                      <div style={{ color: '#334155', fontSize: '12px', lineHeight: '1.5', padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                        💬 {item.comment_text}
                      </div>
                    )}
                    {hasError && item.error && (
                      <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>{item.error}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </ResultCard>
        );
      }

      // Simple mode: single comment
      return (
        <ResultCard title="Bình luận">
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            {d.post_content_snippet && (
              <div style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.5', padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nội dung bài viết</div>
                {d.post_content_snippet.length > 200 ? `${d.post_content_snippet.slice(0, 200)}…` : d.post_content_snippet}
              </div>
            )}
            {d.comment_text && (
              <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#15803d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bình luận đã gửi</div>
                <div style={{ color: '#334155', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{d.comment_text}</div>
              </div>
            )}
            {d.post_url && (
              <a href={d.post_url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontWeight: 500 }}>
                Xem bài viết <ExternalLink size={12} />
              </a>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'reply_to_comments' && (data || result)) {
      const d = data || result;
      // Managed mode
      if ((d.managed || d.group_posts) && d.results) {
        return (
          <ResultCard title={`Trả lời comment — ${d.replied ?? 0} bài có reply, ${d.skipped ?? 0} bỏ qua`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {d.results.map((item: any, idx: number) => {
                const isSkipped = item.skipped;
                const replies: any[] = item.replies || [];
                return (
                  <div key={idx} style={{
                    padding: '10px 12px',
                    background: isSkipped ? '#fefce8' : 'white',
                    borderRadius: '8px',
                    border: `1px solid ${isSkipped ? '#fde68a' : '#e5e7eb'}`,
                    fontSize: '12px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: replies.length > 0 ? '8px' : 0 }}>
                      <span style={{ fontWeight: 600, color: isSkipped ? '#92400e' : '#16a34a' }}>
                        {isSkipped ? '⏭ Bỏ qua' : `✅ ${item.replies_posted ?? 0} reply`}
                        {isSkipped && item.reason && <span style={{ fontWeight: 400, color: '#a16207' }}> ({item.reason})</span>}
                      </span>
                      {item.post_url && (
                        <a href={item.post_url} target="_blank" rel="noopener noreferrer"
                          style={{ color: '#2563eb', fontSize: '11px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 8px', background: '#eff6ff', borderRadius: '6px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                          Mở <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    {replies.map((r: any, rIdx: number) => (
                      <div key={rIdx} style={{ marginTop: '6px', padding: '6px 10px', background: '#f8fafc', borderRadius: '6px', borderLeft: '2px solid #e2e8f0' }}>
                        {r.comment_author && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>💬 {r.comment_author}: <em>{r.comment_text?.length > 80 ? `${r.comment_text.slice(0, 80)}…` : r.comment_text}</em></div>}
                        {r.reply_text && <div style={{ fontSize: '12px', color: '#334155', padding: '4px 8px', background: '#f0fdf4', borderRadius: '5px', border: '1px solid #bbf7d0', marginTop: '4px' }}>↩ {r.reply_text}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </ResultCard>
        );
      }

      // Specific mode
      const replies: any[] = d.replies || [];
      return (
        <ResultCard title={`Trả lời comment — ${d.replied_posted ?? 0}/${d.max_replies ?? 0} reply`}>
          <div style={{ display: 'grid', gap: '8px' }}>
            {replies.map((r: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                {r.comment_author && (
                  <div style={{ color: '#64748b', marginBottom: '6px', padding: '6px 8px', background: '#f8fafc', borderRadius: '6px', borderLeft: '2px solid #e2e8f0' }}>
                    <span style={{ fontWeight: 600 }}>💬 {r.comment_author}</span>
                    {r.comment_text && <div style={{ color: '#94a3b8', marginTop: '2px', fontStyle: 'italic' }}>{r.comment_text.length > 120 ? `${r.comment_text.slice(0, 120)}…` : r.comment_text}</div>}
                  </div>
                )}
                {r.reply_text && (
                  <div style={{ padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', color: '#334155', lineHeight: '1.5' }}>
                    ↩ {r.reply_text}
                  </div>
                )}
              </div>
            ))}
            {replies.length === 0 && !d.replies_posted && (
              <div style={{ color: '#94a3b8', fontSize: '12px' }}>Không có reply nào được ghi lại</div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'interact' && (data || result)) {
      const d = data || result;
      const counts = { likes: d.likes ?? 0, shares: d.shares ?? 0, comments: d.comments ?? 0 };
      const total = counts.likes + counts.shares + counts.comments;
      return (
        <ResultCard title={`Tương tác newsfeed — ${total} hành động`}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {counts.likes > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '18px' }}>👍</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>{counts.likes}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Like</div>
                </div>
              </div>
            )}
            {counts.shares > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '18px' }}>🔁</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>{counts.shares}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Share</div>
                </div>
              </div>
            )}
            {counts.comments > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '18px' }}>💬</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>{counts.comments}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Bình luận</div>
                </div>
              </div>
            )}
            {total === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>Không có tương tác nào được ghi nhận</div>}
          </div>
          {d.type && <div style={{ marginTop: '8px', fontSize: '11px', color: '#94a3b8' }}>Mode: {d.type}</div>}
        </ResultCard>
      );
    }

    if (node.node_type === 'share_url' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Chia sẻ URL" variant="info">
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            {d.original_url && (
              <div style={{ color: '#475569', wordBreak: 'break-all' }}>
                <span style={{ fontWeight: 600 }}>Nguồn:</span>
                <a href={d.original_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', marginLeft: '6px', textDecoration: 'none' }}>
                  {d.original_url}
                </a>
              </div>
            )}
            {(d.share_text || d.caption) && (
              <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nội dung kèm theo</div>
                <div style={{ color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '13px' }}>
                  {d.share_text || d.caption}
                </div>
              </div>
            )}
            {(d.url || d.post_url) && (
              <a href={d.url || d.post_url} target="_blank" rel="noopener noreferrer"
                style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontWeight: 500 }}>
                Xem bài viết đã chia sẻ <ExternalLink size={12} />
              </a>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'search_groups' && (data || result)) {
      const d = data || result;
      const groups = d.groups || [];
      const count = d.groups_found ?? groups.length;
      return (
        <ResultCard title={`Found ${count} groups`}>
          {groups.length > 0 ? (
            <div style={{ display: 'grid', gap: '4px', fontSize: '13px' }}>
              {groups.slice(0, 5).map((group: any, idx: number) => (
                <div key={idx} style={{ color: '#334155' }}>• {group.group_name || group.name || 'Group'} ({group.member_count?.toLocaleString()} members)</div>
              ))}
              {groups.length > 5 && <div style={{ color: '#94a3b8' }}>...and {groups.length - 5} more</div>}
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>No groups found.</div>
          )}
        </ResultCard>
      );
    }

    if (node.node_type === 'join_groups' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      const joined = d.joined_count ?? d.groups_joined ?? results.filter((r: any) => r.success).length;
      const attempted = d.groups_attempted ?? results.length;
      const allFailed = attempted > 0 && joined === 0;

      return (
        <ResultCard title={`Join Groups — ${joined} groups joined`} variant={allFailed ? 'danger' : joined > 0 ? 'success' : 'info'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {results.length > 0 ? (
              results.slice(0, 10).map((item: any, idx: number) => (
                <div key={idx} style={{ padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#1e293b', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                    {item.group_url || item.url || 'Group'}
                  </span>
                  <span style={{ color: item.success ? '#16a34a' : '#ef4444', fontWeight: 600, fontSize: '11px' }}>
                    {item.success ? 'Joined' : 'Failed'}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ fontSize: '13px', color: '#334155' }}>
                Joined {joined} of {attempted} groups
              </div>
            )}
            {results.length > 10 && <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>...and {results.length - 10} more</div>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'find_top_posts' && (data || result)) {
      const d = data || result;
      const posts = d.top_posts || d.leads || d.posts || [];
      return (
        <ResultCard title={`Top Posts — Found ${d.posts_found ?? posts.length} posts`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {posts.length > 0 ? (
              posts.map((post: any, idx: number) => (
                <div key={idx} style={{ padding: '12px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{post.author || `Post #${idx + 1}`}</span>
                    {post.url && (
                      <a href={post.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', padding: '2px 8px', background: '#eff6ff', borderRadius: '6px', textDecoration: 'none' }}>
                        Open <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                  {post.text && <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px', lineHeight: '1.4' }}>{post.text.length > 150 ? `${post.text.slice(0, 150)}…` : post.text}</div>}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#64748b' }}><ThumbsUp size={12} /> {post.likes_count ?? post.likes ?? 0}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#64748b' }}><MessageSquare size={12} /> {post.comments_count ?? post.comments ?? 0}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#64748b' }}><Share2 size={12} /> {post.shares_count ?? post.shares ?? 0}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>No posts found.</div>
            )}
          </div>
        </ResultCard>
      );
    }

    if ((node.node_type === 'like_posts' || node.node_type === 'like_pages') && (data || result)) {
      const d = data || result;
      const count = d.liked_count ?? d.count ?? (d.urls?.length) ?? 0;
      const typeLabel = node.node_type === 'like_posts' ? 'posts' : 'pages';
      return (
        <ResultCard title={`${node.node_type === 'like_posts' ? 'Like Posts' : 'Like Pages'} — ${count} ${typeLabel} liked`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {d.results?.map((r: any, idx: number) => {
              const success = r.success !== false && !r.error;
              const hasError = !!r.error;
              return (
                <div key={idx} style={{
                  padding: '10px 12px',
                  background: hasError ? '#fef2f2' : success ? 'white' : '#fefce8',
                  borderRadius: '8px',
                  border: `1px solid ${hasError ? '#fecaca' : success ? '#e5e7eb' : '#fde68a'}`,
                  fontSize: '12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Heart size={13} fill={success ? '#ef4444' : 'none'} color={success ? '#ef4444' : '#94a3b8'} style={{ flexShrink: 0 }} />
                    <span style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.url || r.id || 'Item'}
                    </span>
                    {hasError && (
                      <span style={{ color: '#dc2626', fontSize: '11px', flexShrink: 0 }}>❌ {r.error}</span>
                    )}
                  </div>
                  {(r.url || r.id) && (
                    <a href={r.url || r.id} target="_blank" rel="noopener noreferrer"
                      style={{
                        color: '#2563eb', fontSize: '11px', fontWeight: 500,
                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                        padding: '2px 8px', background: '#eff6ff', borderRadius: '6px',
                        textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                      Mở <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              );
            }) || (
              <div style={{ fontSize: '13px', color: '#64748b' }}>Successfully processed {count} {typeLabel}.</div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'post_to_fanpage' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Đăng bài Fanpage" variant="success">
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            {d.post_content && (
              <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>Nội dung</div>
                <div style={{ color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{d.post_content}</div>
              </div>
            )}
            {d.post_url && (
              <a href={d.post_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontWeight: 500 }}>
                Xem trên Facebook <ExternalLink size={12} />
              </a>
            )}
            {d.page_name && <div style={{ fontSize: '11px', color: '#94a3b8' }}>Page: {d.page_name}</div>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_comment_on_post' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      return (
        <ResultCard title={`Fanpage Comment — ${d.commented ?? results.length} bình luận`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 600, color: '#16a34a' }}>✅ {item.page_name || 'Fanpage'}</span>
                  {item.post_url && <a href={item.post_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}><ExternalLink size={12} /></a>}
                </div>
                {item.comment_text && <div style={{ color: '#334155', padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px' }}>💬 {item.comment_text}</div>}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_reply_to_comments' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      return (
        <ResultCard title={`Fanpage Reply — ${d.replied ?? results.length} phản hồi`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ fontWeight: 600, color: '#16a34a', marginBottom: '6px' }}>✅ {item.page_name || 'Fanpage'}</div>
                {item.replies?.map((r: any, rIdx: number) => (
                  <div key={rIdx} style={{ marginTop: '4px', padding: '6px 10px', background: '#f8fafc', borderRadius: '6px', borderLeft: '2px solid #e2e8f0' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Khách: {r.comment_author}</div>
                    <div style={{ color: '#334155', marginTop: '2px' }}>↩ {r.reply_text}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_like_comments' && (data || result)) {
      const d = data || result;
      const results = d.results || d.likes || [];
      return (
        <ResultCard title={`Fanpage Like Comments — ${d.liked ?? results.length} lượt like`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{item.page_name || item.page_id || 'Fanpage'}</span>
                  <span style={{ fontSize: '11px', color: '#16a34a', background: '#f0fdf4', padding: '2px 6px', borderRadius: '4px' }}>Liked</span>
                </div>
                <div style={{ marginTop: '6px', color: '#64748b' }}>Comment: {item.comment_id || item.target_comment_id || 'N/A'}</div>
                {item.comment_message && <div style={{ marginTop: '6px', color: '#334155', background: '#f8fafc', padding: '8px 10px', borderRadius: '6px' }}>{item.comment_message}</div>}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_moderate_comments' && (data || result)) {
      const d = data || result;
      const results = d.results || d.moderated || [];
      return (
        <ResultCard title={`Fanpage Moderate Comments — ${d.processed ?? results.length} actions`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{item.page_name || item.page_id || 'Fanpage'}</span>
                  <span style={{ fontSize: '11px', color: '#ea580c', background: '#fff7ed', padding: '2px 6px', borderRadius: '4px' }}>{item.action || d.action || 'moderated'}</span>
                </div>
                <div style={{ marginTop: '6px', color: '#64748b' }}>Comment: {item.comment_id || item.target_comment_id || 'N/A'}</div>
                {item.comment_message && <div style={{ marginTop: '6px', color: '#334155', background: '#f8fafc', padding: '8px 10px', borderRadius: '6px' }}>{item.comment_message}</div>}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_private_reply' && (data || result)) {
      const d = data || result;
      const results = d.results || d.messages || [];
      return (
        <ResultCard title={`Fanpage Private Reply — ${d.sent ?? results.length} tin nhắn`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{item.customer_name || item.comment_author || item.from_name || 'Customer'}</span>
                  <span style={{ fontSize: '11px', color: '#2563eb', background: '#eff6ff', padding: '2px 6px', borderRadius: '4px' }}>Private reply</span>
                </div>
                {(item.comment_message || item.comment_text) && <div style={{ marginTop: '6px', color: '#64748b' }}>Comment: {item.comment_message || item.comment_text}</div>}
                {(item.message_text || item.private_reply_text || item.reply_text) && <div style={{ marginTop: '6px', color: '#334155', background: '#eff6ff', padding: '8px 10px', borderRadius: '6px' }}>✉ {item.message_text || item.private_reply_text || item.reply_text}</div>}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'fanpage_message_customer' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      return (
        <ResultCard title={`Fanpage Message — ${d.sent ?? results.length} tin nhắn`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}><User size={12} style={{ display: 'inline', marginRight: '4px' }} /> {item.customer_name || 'Customer'}</span>
                  <span style={{ fontSize: '11px', color: '#16a34a', background: '#f0fdf4', padding: '2px 6px', borderRadius: '4px' }}>Sent</span>
                </div>
                {item.message_text && <div style={{ color: '#64748b', marginTop: '6px', fontStyle: 'italic' }}>"{item.message_text}"</div>}
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'check_message' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Check Messages" variant="info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ padding: '10px 16px', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>{d.unread_count ?? 0}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>Unread</div>
            </div>
            <div style={{ flex: 1, fontSize: '13px', color: '#475569' }}>
              {(d.unread_count ?? 0) > 0 ? `Found ${d.unread_count} new messages in inbox.` : 'No new messages found.'}
            </div>
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'send_direct_message' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      return (
        <ResultCard title={`Send DM — ${d.sent ?? results.length} sent`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#1e293b' }}>To: <strong>{item.recipient || 'User'}</strong></span>
                <span style={{ color: item.success ? '#16a34a' : '#ef4444', fontWeight: 600 }}>{item.success ? 'Success' : 'Failed'}</span>
              </div>
            ))}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'post_auto_responder' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Post Auto Responder">
          <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1, padding: '8px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, color: '#334155' }}>{d.comments_scanned ?? 0}</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Scanned</div>
              </div>
              <div style={{ flex: 1, padding: '8px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, color: '#16a34a' }}>{d.replies_sent ?? 0}</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>Replies</div>
              </div>
              <div style={{ flex: 1, padding: '8px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, color: '#2563eb' }}>{d.dms_sent ?? 0}</div>
                <div style={{ fontSize: '10px', color: '#94a3b8' }}>DMs</div>
              </div>
            </div>
            {d.post_url && <a href={d.post_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '12px', textDecoration: 'none' }}>Target Post <ExternalLink size={12} /></a>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'group_random_messenger' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Group Random Messenger">
          <div style={{ fontSize: '13px', color: '#475569' }}>
            Sent messages to <strong>{d.sent_count ?? 0}</strong> random members in group.
            {d.group_url && <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>Group: {d.group_url}</div>}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'view_notifications' && (data || result)) {
      const d = data || result;
      const notifications = d.notifications || d.results || [];
      const count = d.notification_count ?? d.notifications_found ?? notifications.length;
      return (
        <ResultCard title="View Notifications" variant="info">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: notifications.length > 0 ? '12px' : '0' }}>
            <Bell size={20} style={{ color: '#6366f1' }} />
            <div style={{ fontSize: '13px', color: '#475569' }}>
              Checked <strong>{count}</strong> notifications.
              {(d.interacted_count ?? 0) > 0 && <span> Interacted with {d.interacted_count} of them.</span>}
            </div>
          </div>
          {notifications.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {notifications.map((notif: any, idx: number) => (
                <div key={idx} style={{ padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{notif.title || notif.author || 'Notification'}</span>
                    {notif.url && <a href={notif.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}><ExternalLink size={12} /></a>}
                  </div>
                  {(notif.body || notif.text) && <div style={{ color: '#475569', lineHeight: '1.4' }}>{notif.body || notif.text}</div>}
                  {notif.type && <div style={{ marginTop: '4px', fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase' }}>{notif.type}</div>}
                </div>
              ))}
            </div>
          )}
        </ResultCard>
      );
    }

    if (node.node_type === 'accept_friend_requests' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="Accept Friend Requests">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <UserPlus size={20} style={{ color: '#16a34a' }} />
            <div style={{ fontSize: '13px', color: '#475569' }}>
              Accepted <strong>{d.accepted_count ?? 0}</strong> friend requests.
            </div>
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'search_add_friend' && (data || result)) {
      const d = data || result;
      const results = d.results || [];
      const addedCount = d.added_count ?? d.added ?? results.length;
      return (
        <ResultCard title={`Search & Add Friend — ${addedCount} added`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {d.keywords && d.keywords.length > 0 && (
              <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px' }}>
                Keywords: <strong>{d.keywords.join(', ')}</strong>
              </div>
            )}
            {results.map((item: any, idx: number) => (
              <div key={idx} style={{ padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#1e293b' }}>{item.name || item.uid || 'User'}</span>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>Sent</span>
              </div>
            ))}
            {results.length === 0 && addedCount === 0 && <div style={{ fontSize: '12px', color: '#94a3b8' }}>No users found for given keywords.</div>}
            {results.length === 0 && addedCount > 0 && <div style={{ fontSize: '12px', color: '#475569' }}>Successfully sent {addedCount} friend requests.</div>}
          </div>
        </ResultCard>
      );
    }

    if ((node.node_type === 'ai_generate_asset' || node.node_type === 'ai_remake_image' || node.node_type === 'ai_grok_remake_image') && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title={node.node_type === 'ai_generate_asset' ? 'AI Generate Asset' : 'AI Remake Image'} variant="success">
          <div style={{ display: 'grid', gap: '8px' }}>
            {d.asset_url && (
              <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <img src={d.asset_url} alt="Generated Asset" style={{ width: '100%', height: 'auto', display: 'block' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px', background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '10px', backdropFilter: 'blur(4px)' }}>
                  Asset ID: {d.asset_id || 'N/A'}
                </div>
              </div>
            )}
            {!d.asset_url && d.asset_id && (
              <div style={{ padding: '10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Wand2 size={16} style={{ color: '#16a34a' }} />
                <div style={{ fontSize: '12px', color: '#15803d' }}>
                  Asset created successfully: <strong>{d.asset_id}</strong>
                </div>
              </div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (node.node_type === 'ai_text_process' && (data || result)) {
      const d = data || result;
      return (
        <ResultCard title="AI Text Process">
          <div style={{ display: 'grid', gap: '10px', fontSize: '13px' }}>
            {d.input_text && (
              <div style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase' }}>Input</div>
                <div style={{ color: '#64748b', fontStyle: 'italic' }}>{d.input_text}</div>
              </div>
            )}
            {d.output_text && (
              <div style={{ padding: '8px 10px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#15803d', marginBottom: '4px', textTransform: 'uppercase' }}>Output</div>
                <div style={{ color: '#1e293b', fontWeight: 500 }}>{d.output_text}</div>
              </div>
            )}
          </div>
        </ResultCard>
      );
    }

    if (data || result.success !== undefined) {
      const isFailed = result.success === false || data?.success === false;
      return (
        <ResultCard title="Result" variant={isFailed ? 'danger' : 'success'}>
          <CollapsibleJSON data={data || result} />
        </ResultCard>
      );
    }

    return null;
  };

  // Execution-level duration
  const execDuration = (() => {
    if (!execution.started_at || !execution.completed_at) return null;
    const ms = new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime();
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s}s`;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* ── Execution Overview Card ──────────── */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}>
        {/* Gradient top bar */}
        <div style={{ height: '4px', background: cfg.gradient }} />

        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
            <div style={{
              width: '44px', height: '44px',
              borderRadius: '12px',
              background: cfg.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: cfg.color,
              flexShrink: 0,
            }}>
              {cfg.icon(22)}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                {execution.workflow_name}
              </h3>
              <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                {execution.execution_id}
              </span>
            </div>
            <span style={{
              fontSize: '12px', fontWeight: 700,
              color: cfg.color, background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderRadius: '8px', padding: '5px 12px',
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {hasError && execution.status !== 'failed' ? 'error' : execution.status}
            </span>
          </div>

          {/* Meta info pills */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', color: '#64748b',
              background: '#f8fafc', borderRadius: '8px', padding: '6px 12px',
            }}>
              <Timer size={14} style={{ color: '#94a3b8' }} />
              {execDuration || 'In progress...'}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', color: '#64748b',
              background: '#f8fafc', borderRadius: '8px', padding: '6px 12px',
            }}>
              <Clock size={14} style={{ color: '#94a3b8' }} />
              {formatDateTime(execution.started_at)}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '12px', color: '#64748b',
              background: '#f8fafc', borderRadius: '8px', padding: '6px 12px',
            }}>
              <Hash size={14} style={{ color: '#94a3b8' }} />
              {execution.nodes.length} nodes
            </div>
          </div>
        </div>
      </div>

      {/* ── Nodes Timeline ───────────────────── */}
      <div>
        <h3 style={{
          fontSize: '15px', fontWeight: 700, color: '#0f172a',
          marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <span style={{
            width: '24px', height: '24px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 700,
          }}>
            {execution.nodes.length}
          </span>
          Execution Timeline
        </h3>

        <div style={{ position: 'relative' }}>
          {/* Vertical timeline line */}
          <div style={{
            position: 'absolute',
            left: '19px',
            top: '24px',
            bottom: '24px',
            width: '2px',
            background: 'linear-gradient(to bottom, #e2e8f0, #f1f5f9)',
            borderRadius: '1px',
          }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {execution.nodes.map((node, index) => {
              const hasError = !!node.error || node.status === 'failed';
              const nodeCfg = hasError ? STATUS_CFG.failed : getCfg(node.status);
              return (
                <div key={`${node.node_id}-${index}`} style={{
                  display: 'flex',
                  gap: '16px',
                  position: 'relative',
                }}>
                  {/* Timeline dot */}
                  <div style={{
                    width: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    paddingTop: '18px',
                    flexShrink: 0,
                    zIndex: 1,
                  }}>
                    <div style={{
                      width: '14px', height: '14px',
                      borderRadius: '50%',
                      background: 'white',
                      border: `3px solid ${nodeCfg.dotColor}`,
                      boxShadow: `0 0 0 3px ${nodeCfg.bg}`,
                    }} />
                  </div>

                  {/* Node card */}
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    background: 'white',
                    borderRadius: '14px',
                    border: '1px solid #f1f5f9',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    padding: '16px 18px',
                    transition: 'border-color 0.2s',
                  }}>
                    {/* Node header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '28px', height: '28px',
                        borderRadius: '8px',
                        background: nodeCfg.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}>
                        {index + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b', wordBreak: 'break-word' }}>
                          {node.node_label}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {node.node_type}
                        </div>
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600,
                        color: nodeCfg.color, background: nodeCfg.bg,
                        border: `1px solid ${nodeCfg.border}`,
                        borderRadius: '6px', padding: '3px 8px',
                        textTransform: 'uppercase',
                      }}>
                        {hasError && node.status !== 'failed' ? 'error' : node.status}
                      </span>
                    </div>

                    {/* Duration / time */}
                    <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#94a3b8' }}>
                      {node.duration_seconds !== null && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Timer size={12} /> {formatDuration(node.duration_seconds)}
                        </span>
                      )}
                      {node.started_at && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={12} /> {format(new Date(node.started_at), 'HH:mm:ss')}
                        </span>
                      )}
                    </div>

                    {/* Result data */}
                    {renderNodeResult(node)}

                    {/* Error */}
                    {node.error && (
                      <div style={{
                        marginTop: '10px',
                        padding: '10px 12px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '10px',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-start',
                      }}>
                        <AlertCircle size={14} style={{ color: '#ef4444', marginTop: '1px', flexShrink: 0 }} />
                        <div style={{ fontSize: '12px', color: '#dc2626', lineHeight: '1.4' }}>
                          {node.error}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {execution.nodes.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '16px',
            border: '1px dashed #cbd5e1',
          }}>
            <Clock size={40} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
            <p style={{ color: '#94a3b8', fontSize: '14px' }}>No node execution data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
